import * as ngCore from '@angular/core'
import { MatTooltip } from '@angular/material/tooltip'

/**
 * Directive that shows a tooltip **only if the element's text is truncated** (i.e., ellipsis is applied).
 *
 * This directive wraps Angular Material's {@link MatTooltip}.
 *
 *  NOTE: For ellipsis detection to work properly, the host element must have a **fixed or constrained width**.
 * Without it, the element may expand to fit its content, preventing truncation and suppressing the tooltip.
 *
 * **Inputs**
 * - `atnDsEllipsisTooltip`: Tooltip text to show when the element is truncated.
 * - `atnDsEllipsisTooltipPosition`: Optional tooltip position.
 *
 * @example
 * ```html
 * <div
 *   [atnDsEllipsisTooltip]="item.label"
 *   [atnDsEllipsisTooltipPosition]="'above'"
 *   class="truncate"
 * >
 *   {{ item.label }}
 * </div>
 * ```
 */
@ngCore.Directive({
  hostDirectives: [
    {
      directive: MatTooltip,
      inputs: [
        'matTooltip: atnDsEllipsisTooltip',
        'matTooltipPosition: atnDsEllipsisTooltipPosition',
      ],
    },
  ],
  selector: '[atnDsEllipsisTooltip]',
})
export class AtnDsEllipsisTooltipDirective {
  /**
   * Observer that watches for element size changes.
   */
  private _observer?: ResizeObserver

  private get _el(): HTMLElement {
    return this._elRef.nativeElement
  }

  constructor(
    private readonly _elRef: ngCore.ElementRef<HTMLElement>,
    private readonly _tooltip: MatTooltip,
    private readonly _destroyRef: ngCore.DestroyRef,
  ) {
    ngCore.afterNextRender(() => {
      // Apply styles to enable ellipsis
      this._el.style.overflow = 'hidden'
      this._el.style.textOverflow = 'ellipsis'
      this._el.style.whiteSpace = 'nowrap'

      this._observer = new ResizeObserver(() => {
        this._tooltip.message =
          this._el.scrollWidth > this._el.clientWidth
            ? this._tooltip.message
            : ''
      })

      this._observer.observe(this._el)
    })

    this._destroyRef.onDestroy(() => {
      this._observer?.disconnect()
    })
  }
}
